import { motion } from 'framer-motion';
import { ArrowRight, Award, BadgeCheck, Coins, FileText, PlayCircle, ShieldCheck } from 'lucide-react';
import Container from '../ui/Container';
import Button from '../ui/Button';
import StatCounter from '../ui/StatCounter';
import useReducedMotion from '../../hooks/useReducedMotion';
import { impactStats } from '../../data/stats';

const complianceChips = [
  { icon: FileText, label: 'Form 6 Issuance' },
  { icon: BadgeCheck, label: 'Passbook Entry' },
  { icon: Coins, label: 'EPR Credits' },
  { icon: Award, label: 'Recycling Certificates' },
];

/** Documents shown in the floating "compliance dossier" stack. */
const dossier = [
  { icon: FileText, title: 'Form 6', meta: 'Consignment EW-2026-1042', tint: 'from-brand-500 to-brand-700' },
  { icon: Coins, title: 'EPR Credit Note', meta: '1.85 MT credited', tint: 'from-ink-700 to-ink-900' },
  { icon: Award, title: 'Recycling Certificate', meta: '98.6% recovery', tint: 'from-brand-600 to-ink-800' },
];

const heroStats = impactStats.filter((s) => ['companies', 'recycled', 'certificates'].includes(s.id));

export default function Hero() {
  const reducedMotion = useReducedMotion();

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reducedMotion ? 0 : 0.09, delayChildren: 0.05 } },
  };
  const item = reducedMotion
    ? { hidden: {}, show: {} }
    : {
        hidden: { opacity: 0, y: 22 },
        show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
      };

  return (
    <section className="relative overflow-hidden bg-ink-950 pb-20 pt-16 sm:pb-24 sm:pt-20 lg:pb-32 lg:pt-24">
      {/* Layered background: grid, radial glows, bottom fade */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-grid bg-grid-cell opacity-70" />
        <div className="absolute -left-40 -top-32 h-[32rem] w-[32rem] rounded-full bg-brand-600/25 blur-[120px]" />
        <div className="absolute -right-32 top-24 h-[28rem] w-[28rem] rounded-full bg-brand-400/12 blur-[120px]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink-950 to-transparent" />
      </div>

      <Container className="relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          {/* Copy */}
          <motion.div variants={container} initial="hidden" animate="show">
            <motion.span variants={item} className="eyebrow-dark">
              <ShieldCheck className="h-3.5 w-3.5" />
            </motion.span>

            <motion.h1
              variants={item}
              className="mt-6 text-[2.15rem] font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]"
            >
              Dispose of your company&apos;s e-waste with{' '}
              <span className="relative whitespace-nowrap text-brand-400">
                100% compliance
                <svg
                  className="absolute -bottom-2 left-0 h-2.5 w-full text-brand-500/60"
                  viewBox="0 0 300 12"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path d="M2 9C60 3 130 2 298 6" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </motion.h1>

            <motion.p variants={item} className="mt-7 max-w-xl text-base leading-relaxed text-ink-300 sm:text-lg">
              Form 6 issuance, passbook entries, EPR credits, recycling certificates and certified data
              destruction — the full compliance trail, handled end to end by a licensed recycler.
            </motion.p>

            <motion.div variants={item} className="mt-9 flex flex-wrap gap-3">
              <Button to="/request-pickup" size="lg" variant="primary">
                Request a Free Pickup
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button to="/contact" size="lg" variant="onDark">
                <PlayCircle className="h-4 w-4" />
                Talk to a Compliance Expert
              </Button>
            </motion.div>

            <motion.ul variants={item} className="mt-9 flex flex-wrap gap-x-6 gap-y-3">
              {complianceChips.map(({ icon: ChipIcon, label }) => (
                <li key={label} className="flex items-center gap-2 text-sm text-ink-300">
                  <ChipIcon className="h-4 w-4 flex-none text-brand-400" />
                  {label}
                </li>
              ))}
            </motion.ul>

            <motion.dl
              variants={item}
              className="mt-10 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
            >
              {heroStats.map((stat) => (
                <div key={stat.id}>
                  <dt className="sr-only">{stat.label}</dt>
                  <dd className="font-display text-2xl font-bold text-white sm:text-3xl">
                    <StatCounter value={stat.value} suffix={stat.suffix} />
                  </dd>
                  <p className="mt-1 text-xs leading-tight text-ink-500">{stat.label}</p>
                </div>
              ))}
            </motion.dl>
          </motion.div>

          {/* Compliance dossier visual */}
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-md lg:max-w-none"
          >
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-400">
                    Compliance Dossier
                  </p>
                  <p className="mt-1 font-display text-lg font-semibold text-white">
                    Issued with every consignment
                  </p>
                </div>
                <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-500/15 text-brand-300">
                  <ShieldCheck className="h-5 w-5" />
                </span>
              </div>

              <div className="mt-7 space-y-4">
                {dossier.map((doc, index) => (
                  <motion.div
                    key={doc.title}
                    initial={reducedMotion ? false : { opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.45 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-4 rounded-xl2 border border-white/10 bg-ink-900/70 p-4 transition-transform duration-300 hover:translate-x-1"
                  >
                    <span className={`grid h-11 w-11 flex-none place-items-center rounded-xl bg-gradient-to-br ${doc.tint} text-white`}>
                      <doc.icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-display text-sm font-semibold text-white">{doc.title}</p>
                      <p className="truncate text-xs text-ink-500">{doc.meta}</p>
                    </div>
                    <BadgeCheck className="ml-auto h-5 w-5 flex-none text-brand-400" />
                  </motion.div>
                ))}
              </div>

              <div className="mt-7 rounded-xl2 bg-brand-500/10 p-4">
                <p className="text-xs leading-relaxed text-brand-100/85">
                  Documentation delivered within <strong className="font-semibold text-white">7 working days</strong> of
                  processing.
                </p>
              </div>
            </div>

            {/* Floating accent badge */}
            <motion.div
              animate={reducedMotion ? {} : { y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-lift sm:-left-8"
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-700">
                <Award className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-lg font-bold leading-none text-ink-900">98.6%</p>
                <p className="mt-1 text-[0.7rem] font-medium text-ink-500">Material recovery rate</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
